import React, { memo, useState } from 'react';

import {
	Button,
	Confirm,
	Modal,
	SliderCheckbox,
	SpinnerOverlay,
} from '@components/common';

import { useMutateKaiAssistants } from '@hooks/kaiAssistants/useMutateKaiAssistants';
import { useQueryKaiAssistants } from '@hooks/kaiAssistants/useQueryKaiAssistants';

import { IKaiAssistantDTO } from '@interfaces/kai/dto/kaiDTO';
import { IOrganisationProfile } from '@interfaces/organisation/organisations';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { enabledKaiTooltip, hideKaiTooltip, kaiAssistantSelectValues } from './constants';

import CreateKaiAssistant from './CreateKaiAssistant';
import EditKaiAssistant from './EditKaiAssistant';
import KaiAssistantsList from './KaiAssistantsList';

import styles from './ManageKaiAssistant.module.scss';

interface IManageKaiAssistantProps {
	profile: IOrganisationProfile;
	organisationId?: string;
	isPlatformOwner: boolean;
}

const ManageKaiAssistant = ({
	profile,
	organisationId,
	isPlatformOwner,
}: IManageKaiAssistantProps) => {
	const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
	const [isOpenDeleteConfirmationModal, setIsOpenDeleteConfirmationModal] = useState(false);
	const [deletingAssistantId, setDeletingAssistantId] = useState<string | null>(null);
	const [editingAssistant, setEditingAssistant] = useState<IKaiAssistantDTO | null>(null);

	const {
		kaiAssistants,
		isLoadingKaiAssistants,
	} = useQueryKaiAssistants(profile?.id);

	const {
		disableKaiAssistants,
		enableKaiAssistants,
		deleteKaiAssistant,
		hideKaiAssistant,
		unhideKaiAssistant,
		isEnablingKaiAssistant,
		isDisablingKaiAssistant,
		isDeletingKaiAssistant,
	} = useMutateKaiAssistants();

	const handleChangeEnabled = async (_: string, value: boolean) => {
		if (!organisationId) return;

		if (value) {
			await enableKaiAssistants(organisationId);
		} else {
			await disableKaiAssistants(organisationId);
		}
	};

	const handleChangeHidden = async (_: string, value: boolean) => {
		if (!organisationId) {
			return;
		}

		if (value) {
			await hideKaiAssistant(organisationId);
		} else {
			await unhideKaiAssistant(organisationId);
		}
	};

	const openCreateModal = () => setIsOpenCreateModal(true);

	const closeCreateModal = () => setIsOpenCreateModal(false);

	const openDeleteConfirmationModal = (assistantId: string) => () => {
		setDeletingAssistantId(assistantId);
		setIsOpenDeleteConfirmationModal(true);
	};

	const closeDeleteConfirmationModal = () => {
		setIsOpenDeleteConfirmationModal(false);
		setDeletingAssistantId(null);
	};

	const handleDelete = async () => {
		if (!deletingAssistantId) return;

		await deleteKaiAssistant(deletingAssistantId);
		closeDeleteConfirmationModal();
	};

	const openEditModal = (assistant: IKaiAssistantDTO) => () => {
		setEditingAssistant(assistant);
	};

	const closeEditModal = () => setEditingAssistant(null);

	return (
		<div className={styles.kaiAssistantsContainer}>
			<div className={styles.kaiAssistantsCard__enabling}>
				<SliderCheckbox
					checked={profile?.isKaiAssistantsEnabled}
					className='mb-10'
					id={kaiAssistantSelectValues.isKaiAssistantEnabled}
					isLabelAfter
					label='Enable Kai Assistant'
					name={kaiAssistantSelectValues.isKaiAssistantEnabled}
					onChange={handleChangeEnabled}
					tooltipText={enabledKaiTooltip}
				/>
			</div>
			<div className={styles.kaiAssistantsCard}>
				{isLoadingKaiAssistants ? (
					<div>Loading...</div>
				) : (
					<>
						<div className={styles.headerContainer}>
							<div>
								<div className={styles.header}>Assistants connected</div>
								<div>Mark the Kai Assistant(s) to show</div>
							</div>
							<Button
								onClick={openCreateModal}
								secondary
							>
								Add assistant
							</Button>
						</div>
						{!kaiAssistants.length && (
							<div className={styles.emptyText}>No assistants connected yet</div>
						)}
						<KaiAssistantsList
							isEnabled={profile?.isKaiAssistantsEnabled}
							kaiAssistants={kaiAssistants}
							openDeleteConfirmationModal={openDeleteConfirmationModal}
							openEditModal={openEditModal}
						/>
					</>
				)}
			</div>
			{isPlatformOwner
				&& <>
					<div className={styles.divider} />
					<div className={styles.hideKaiAssistant}>
						<SliderCheckbox
							checked={profile.isKaiAssistantHidden}
							id={kaiAssistantSelectValues.isKaiAssistantHidden}
							isLabelAfter
							label='Hide Kai Assistant settings from the Organisation'
							name={kaiAssistantSelectValues.isKaiAssistantHidden}
							onChange={handleChangeHidden}
							tooltipText={hideKaiTooltip}
						/>
					</div>
				</>
			}
			<Modal
				isOpen={isOpenCreateModal}
				onClose={closeCreateModal}
				title='Add assistant'
				width='400px'
			>
				<CreateKaiAssistant
					close={closeCreateModal}
					organizationId={profile?.id}
				/>
			</Modal>
			<Modal
				isLoading={isDeletingKaiAssistant}
				isOpen={isOpenDeleteConfirmationModal}
				onClose={closeDeleteConfirmationModal}
				title='Delete assistant'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeDeleteConfirmationModal}
					onSubmit={handleDelete}
					question='Are you sure you want to delete connected assistant from the organisation?'
				/>
			</Modal>
			{editingAssistant && (
				<Modal
					isOpen={!!editingAssistant}
					onClose={closeEditModal}
					title='Edit assistant'
					width='400px'
				>
					<EditKaiAssistant
						assistant={editingAssistant}
						close={closeEditModal}
						organizationId={profile?.id}
					/>
				</Modal>
			)}
			{isEnablingKaiAssistant || isDisablingKaiAssistant && <SpinnerOverlay />}
		</div>
	);
};

export default memo(ManageKaiAssistant);
